import { compactNumber } from "@/lib/formatNumber";
import { publicOrigin } from "@/lib/publicOrigin";
import { liveTitle } from "@/lib/liveTitle";
import { FavoriteNote } from "@/components/admin/FavoriteNote";
import { ChurchReplayCard, useChurchLiveActions } from "@/components/ChurchReplayCard";
import { ChurchFeaturedReplay } from "@/components/ChurchFeaturedReplay";
import { AdCreditBalance, useAdCredits, toastCreditError } from "@/components/AdCreditBalance";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { ImageViewer } from "@/components/ImageViewer";
import { api } from "@/lib/api";
import { requireLogin } from "@/lib/guestGate";
import { useApp } from "@/context/AppContext";
import { useSeo, excerpt } from "@/lib/seo";
import { usePostListSync } from "@/lib/postSync";
import { resolveMediaUrl, FileUpload } from "@/components/FileUpload";
import { NEUTRAL_POSTER, videoPosterFor } from "@/lib/neutralPoster";
import { ChurchCoverFallback } from "@/components/ChurchCoverFallback";
import { bgStyle } from "@/lib/gradients";
import { storyFontStyle } from "@/lib/storyFonts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { Church, MapPin, ShieldCheck, User, Bell, Check, Users, Globe, ArrowLeft, Megaphone, Pencil, BarChart3, Eye, TrendingUp, Radio, Film, Sparkles, PlayCircle, Share2, Clock, X, UserPlus, CalendarDays, CalendarPlus, CalendarClock, Plus, Trash2, Unlink, Pin, Newspaper, Flame, Star, GitBranch } from "lucide-react";
import { ChurchAnnexes } from "@/components/ChurchAnnexes";

import { DetailSkeleton } from "@/components/FeedSkeleton";
import { PostCard } from "@/components/PostCard";
import { FeedWindow } from "@/components/FeedWindow";
import { PostComposer } from "@/components/PostComposer";
import { StoryComposer } from "@/components/StoryComposer";
import { CoverEditMenu, ImageCropUpload } from "@/components/ImageCropUpload";
import { ChurchInvitePoster } from "@/components/ChurchInvitePoster";
import { shareLinkCard } from "@/lib/nativeShare";
import { countdownInfo } from "@/lib/dateLocale";
import { denominationLabel, weekdayNames } from "@/lib/denominations";
import { ChurchStoryTemplates } from "@/components/ChurchStoryTemplates";
import { StoryCover } from "@/components/StoryStage";
import { ChurchStoryViewer } from "@/components/ChurchStoryViewer";
import { ChurchManagersCard, ChurchManagerInviteBanner } from "@/components/ChurchManagersCard";
import { ChurchActivityCard } from "@/components/ChurchActivityCard";
import { CancelServiceDialog, ServiceCancellationsNotice } from "@/components/ChurchServiceCancel";
import { nextServiceOccurrence, nextOccurrenceOf, cancellationFor, fmtDM } from "@/lib/churchServices";
import { CalendarX2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const timeAgo = (iso, t) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} ${t("time.min", "min")}`;
  if (s < 86400) return `${Math.floor(s / 3600)} ${t("time.hour", "h")}`;
  return `${Math.floor(s / 86400)} ${t("time.day", "j")}`;
};

const fmtTime = (val, lang) => {
  const s = val || "";
  return lang === "fr" ? s.replace(":", "h") : s;
};
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Lot 723 — nextServiceOccurrence vit dans lib/churchServices.js (saute les occurrences annulées).

function fmtCountdown(ms, t) {
  if (ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days >= 1) return `${days} ${t("time.day", "j")} ${hours} ${t("time.hour", "h")}`;
  if (hours >= 1) return `${hours} ${t("time.hour", "h")} ${mins} ${t("time.min", "min")}`;
  return `${mins} ${t("time.min", "min")}`;
}

export default function ChurchDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, t, lang, config } = useApp();
  const WEEKDAYS = weekdayNames(t);
  const [church, setChurch] = useState(null);
  useSeo(church ? {
    title: `${church.name || "Église"} — Église sur Yalola`,
    description: excerpt(church.description, 150) || [church.denomination, church.city, church.country].filter(Boolean).join(" · ") || "Suis les directs, annonces et la vie de la communauté sur Yalola.",
    canonical: `/churches/${church.handle || church.id}`, type: "profile",
    image: `${process.env.REACT_APP_PUBLIC_URL || "https://yalola.app"}/api/e/${church.handle || church.id}/share.jpg`,
  } : null, [church?.id, church?.name, church?.description]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewIdx, setViewIdx] = useState(null); // annonce ouverte dans la visionneuse (index)
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [lives, setLives] = useState([]);
  const [livesTotal, setLivesTotal] = useState(0);
  const LIVES_PREVIEW = 6;
  // Tri de la vidéothèque : « Récents » (épinglés d'abord) ou « Les plus vues » (prédications populaires, pastilles N°1-3).
  const [livesSort, setLivesSort] = useState("recent");
  const livesSortRef = useRef("recent");
  const [featured, setFeatured] = useState(null);
  const [posts, setPosts] = useState([]);
  usePostListSync(setPosts);
  const [attachOpen, setAttachOpen] = useState(false);
  // Couverture / logo en plein écran (lot 577) : { url } ou null.
  const [viewer, setViewer] = useState(null);
  const [myUnattached, setMyUnattached] = useState([]);
  const [attachBusy, setAttachBusy] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ budget: 10, days: 7 });
  const promoteFree = ["super_admin", "manager", "moderator"].includes(user?.role);
  const { data: credits, balance: creditBalance, currency: creditCur, reload: reloadCredits } = useAdCredits(promoteOpen && !!user && !promoteFree);
  const [annOpen, setAnnOpen] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [editScheduled, setEditScheduled] = useState(null);
  const [events, setEvents] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleRows, setScheduleRows] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [eventForm, setEventForm] = useState({ title: "", starts_at: "", location: "", description: "", cover_url: "", is_service: false });
  const [savingEvent, setSavingEvent] = useState(false);
  // Lot 607 : le responsable OU un gestionnaire délégué (church.can_manage) publie au nom de l'église.
  const canPublishAnn = !!church && (church.can_manage || ["super_admin", "manager"].includes(user?.role));
  // Publishing a POST in the name of the church mirrors the backend rule
  // (owner / delegated manager, super_admin or manager) + the publish-as feature flag.
  const canPublishPost = !!church && config?.settings?.features?.preacher_publish_as !== false &&
    (church.can_manage || ["super_admin", "manager"].includes(user?.role));

  const loadEvents = useCallback(() => {
    api.get(`/churches/${id}/events`).then(({ data }) => setEvents(data.events || [])).catch(() => setEvents([]));
  }, [id]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const fmtEventDate = (isoStr) => {
    try { return new Date(isoStr).toLocaleString(lang || "fr", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return isoStr; }
  };

  const openSchedule = () => {
    setScheduleRows((church.service_schedule || []).map((s) => ({ ...s })));
    setScheduleOpen(true);
  };
  const addRow = () => setScheduleRows((r) => [...r, { day: 6, time: "10:00", title: "Culte" }]);
  const updateRow = (i, k, v) => setScheduleRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeRow = (i) => setScheduleRows((r) => r.filter((_, idx) => idx !== i));
  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const clean = scheduleRows
        .filter((r) => TIME_RE.test(r.time))
        .map((r) => ({ day: Number(r.day), time: r.time, title: (r.title || "").trim() }));
      const { data } = await api.patch(`/churches/${id}`, { service_schedule: clean });
      setChurch((c) => ({ ...c, service_schedule: data.church?.service_schedule || clean }));
      toast.success(t("church.agenda_saved", "Agenda mis à jour ✅"));
      setScheduleOpen(false);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setSavingSchedule(false); }
  };

  const createEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.starts_at) { toast.error(t("church.event_need_fields", "Titre et date requis")); return; }
    setSavingEvent(true);
    try {
      const startsIso = new Date(eventForm.starts_at).toISOString();
      const { data } = await api.post(`/events`, { church_id: id, title: eventForm.title.trim(), starts_at: startsIso, location: eventForm.location, description: eventForm.description, cover_url: eventForm.cover_url || null, is_service: !!eventForm.is_service });
      if (data?.story) setAnnouncements((list) => [data.story, ...list]);  // lot 726 — story « Culte exceptionnel » auto
      toast.success(data?.story
        ? t("church.special_service_created_story", "Culte exceptionnel publié + story sur la page — vos abonnés sont prévenus 🔔")
        : eventForm.is_service ? t("church.special_service_created", "Culte exceptionnel publié — vos abonnés sont prévenus 🔔") : t("church.event_created", "Événement créé — vos abonnés sont prévenus 🔔"));
      setEventOpen(false);
      setEventForm({ title: "", starts_at: "", location: "", description: "", cover_url: "", is_service: false });
      loadEvents();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setSavingEvent(false); }
  };
  const toggleAttend = async (ev) => {
    if (!user) { requireLogin("attend"); return; }
    try {
      const { data } = await api.post(`/events/${ev.id}/attend`);
      setEvents((list) => list.map((x) => (x.id === ev.id ? { ...x, is_attending: data.attending, attendee_count: Math.max(0, (x.attendee_count || 0) + (data.attending ? 1 : -1)) } : x)));
    } catch { toast.error(t("common.error", "Erreur")); }
  };
  const deleteEvent = async (ev) => {
    try {
      await api.delete(`/events/${ev.id}`); toast.success(t("church.event_deleted", "Événement supprimé")); loadEvents();
      if (ev.is_service) setAnnouncements((list) => list.filter((s) => s.event_id !== ev.id));  // lot 726 — story auto retirée avec le culte
    }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  // Vidéothèque : 6 cartes (épinglés d'abord) + « Voir tout » → /churches/:id/replays.
  const loadLives = useCallback(() => {
    const sort = livesSortRef.current;
    api.get(`/churches/${id}/lives`, { params: { limit: LIVES_PREVIEW, sort } })
      .then(({ data }) => { if (livesSortRef.current !== sort) return; setLives(data.lives || []); setLivesTotal(data.total || (data.lives || []).length); })
      .catch(() => {});
    // « Prédication vedette » : la rediffusion la plus vue (au moins 1 vue), en grande carte en tête de page.
    api.get(`/churches/${id}/lives`, { params: { limit: 1, sort: "views" } })
      .then(({ data }) => { const top = (data.lives || [])[0]; setFeatured(top && top.status !== "live" && (top.replay_views || 0) >= 1 ? top : null); })
      .catch(() => {});
  }, [id]);
  const changeLivesSort = (k) => { if (k === livesSortRef.current) return; livesSortRef.current = k; setLivesSort(k); loadLives(); };
  const openAttach = () => {
    setAttachOpen(true);
    api.get("/live/mine/unattached").then(({ data }) => setMyUnattached(data.lives || [])).catch(() => setMyUnattached([]));
  };
  const attachMyLive = async (liveId) => {
    setAttachBusy(liveId);
    try {
      await api.post(`/churches/${id}/attach-live`, { live_id: liveId });
      toast.success(t("church.live_attached", "Direct rattaché à cette église ✅"));
      setMyUnattached((list) => list.map((x) => x.id === liveId ? { ...x, church_id: id, church_name: church.name } : x));
      loadLives();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setAttachBusy(""); }
  };
  const liveActions = useChurchLiveActions({
    churchId: id, t,
    onChanged: (kind, l) => {
      if (kind === "remove") { setLives((list) => list.filter((x) => x.id !== l.id)); setLivesTotal((n) => Math.max(0, n - 1)); }
      loadLives();
    },
  });

  const cancellations = useMemo(() => church?.service_cancellations || [], [church?.service_cancellations]);
  const nextService = useMemo(() => nextServiceOccurrence(church?.service_schedule, cancellations, new Date(), events), [church?.service_schedule, cancellations, events]);
  const [cancelTarget, setCancelTarget] = useState(null); // lot 723 — {item, date} du culte à annuler
  const onCancelled = (c) => setChurch((ch) => ({ ...ch, service_cancellations: [...(ch.service_cancellations || []), c].sort((a, b) => a.date.localeCompare(b.date)) }));
  const onRestored = (c) => setChurch((ch) => ({ ...ch, service_cancellations: (ch.service_cancellations || []).filter((x) => x.id !== c.id) }));
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!nextService) return undefined;
    const iv = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(iv);
  }, [nextService]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/churches/${id}`);
      setChurch(data.church);
      setAnnouncements(data.announcements || []);
    } catch { setChurch(false); }
    loadLives();
    api.get(`/churches/${id}/posts`).then(({ data }) => setPosts(data.posts || [])).catch(() => {});
    setLoading(false);
  }, [id, loadLives]);
  useEffect(() => { load(); }, [load]);

  const loadScheduled = useCallback(() => {
    api.get(`/churches/${id}/scheduled-announcements`).then(({ data }) => setScheduled(data.stories || [])).catch(() => setScheduled([]));
  }, [id]);
  useEffect(() => {
    if (church && canPublishAnn) loadScheduled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [church?.id, canPublishAnn]);
  const cancelScheduled = async (sid) => {
    try {
      await api.delete(`/churches/${id}/scheduled-announcements/${sid}`);
      toast.success(t("church.announcement_cancelled", "Annonce programmée annulée"));
      loadScheduled();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  useEffect(() => {
    if (church && church.can_edit && church.stats_enabled) {
      api.get(`/churches/${id}/stats`).then(({ data }) => setStats(data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [church?.id, church?.can_edit, church?.stats_enabled, id]);

  if (loading) return <AppShell showSidebars={false}><DetailSkeleton /></AppShell>;
  if (!church) {
    return (
      <AppShell showSidebars={false}>
        <div className="yalola-card p-10 max-w-lg mx-auto text-center space-y-4" data-testid="church-not-found">
          <div className="h-16 w-16 rounded-2xl grid place-items-center mx-auto" style={{ background: "var(--yalola-surface-alt)" }}>
            <Church size={30} style={{ color: "var(--yalola-text-muted)" }} />
          </div>
          <div>
            <h1 className="text-lg font-bold font-display">{t("church.not_found_title", "Église introuvable")}</h1>
            <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>
              {t("church.not_found_desc", "Cette page d'église n'existe plus ou le lien est incorrect.")}
            </p>
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button className="rounded-full pill-primary" onClick={() => nav("/churches")} data-testid="church-not-found-browse">
              {t("church.not_found_browse", "Découvrir les églises")}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => nav("/")} data-testid="church-not-found-home">
              {t("church.not_found_home", "Retour au fil")}
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  const toggleFollow = async () => {
    if (!user) { requireLogin("follow"); return; }
    try {
      const { data } = await api.post(`/churches/${id}/follow`);
      setChurch((c) => ({ ...c, is_following: data.following, followers_count: data.followers_count }));
      toast.success(data.following ? t("church.followed", "Tu suis {name} 🔔").replace("{name}", church.name) : t("church.unfollowed", "Tu ne suis plus cette église"));
    } catch { toast.error(t("common.error", "Erreur")); }
  };

  const openEdit = () => {
    setForm({
      handle: church.handle || "",
      share_accent: church.share_accent || "foret",
      logo_url: church.logo_url || "", cover_url: church.cover_url || "", cover_video_url: church.cover_video_url || "",
      description: church.description || "", address: church.address || "", website: church.website || "",
      pastor_title: church.pastor_title || "", pastor_name: church.pastor_name || "",
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/churches/${id}`, form);
      setChurch((c) => ({ ...c, ...data.church }));
      toast.success(t("church.updated_toast", "Fiche mise à jour ✅"));
      setEditing(false);
      // If the custom URL (handle) changed, keep the browser URL valid.
      if (data.church?.handle && data.church.handle !== id) {
        nav(`/churches/${data.church.handle}`, { replace: true });
      }
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setSaving(false); }
  };

  const toggleFeature = async () => {
    try {
      const { data } = await api.post(`/churches/${id}/feature`);
      setChurch((c) => ({ ...c, featured: data.featured }));
      toast.success(data.featured ? t("church.featured_toast_on", "Église mise à la une ✨") : t("church.featured_toast_off", "Retirée de la une"));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const toggleFavorite = async () => {
    try {
      const { data } = await api.post(`/admin/churches/${church.id}/favorite`);
      setChurch((c) => ({ ...c, is_favorite: data.favorite, favorite_note: "" }));
      toast.success(data.favorite ? "Ajoutée à tes favoris ★" : "Retirée de tes favoris");
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const doPromote = async () => {
    setPromoting(true);
    try {
      await api.post(`/churches/${id}/promote`, { budget: Number(promoteForm.budget) || 0, days: Number(promoteForm.days) || 7 });
      toast.success(t("church.campaign_launched", "Campagne lancée 🎉 Ta page apparaît maintenant comme sponsorisée."));
      reloadCredits();
      setPromoteOpen(false);
      setPromoting(false);
    } catch (e) { toastCreditError(e, t, nav); setPromoting(false); }
  };
  const setCover = async (url) => {
    try { await api.patch(`/churches/${id}`, { cover_url: url }); toast.success(t("church.cover_updated", "Couverture mise à jour")); load(); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const removeCover = async () => {
    try { await api.patch(`/churches/${id}`, { cover_url: null }); toast.success(t("church.cover_removed", "Couverture supprimée")); load(); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const setLogo = async (url) => {
    try { await api.patch(`/churches/${id}`, { logo_url: url }); toast.success(t("church.logo_updated", "Logo mis à jour")); load(); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  const liveNow = lives.find((l) => l.status === "live");

  return (
    <AppShell showSidebars={false}>
      <div className="max-w-[900px] mx-auto space-y-4" data-testid="church-detail-page">
        {church.my_manager_invite && config?.settings?.features?.church_managers !== false && <ChurchManagerInviteBanner church={church} onDone={load} />}
        {/* Header */}
        <div className="yalola-card overflow-hidden">
          <div className="relative h-40 md:h-52" style={{ background: "var(--gradient-navy)" }}>
            {church.cover_video_url ? (
              <video src={resolveMediaUrl(church.cover_video_url)} poster={church.cover_url ? resolveMediaUrl(church.cover_url) : undefined} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="church-cover-video" />
            ) : church.cover_url ? (
              // Couverture cliquable (lot 577) : plein écran + télécharger / partager — Retour et menu d'édition restent au-dessus (z-20).
              <button type="button" className="absolute inset-0 w-full h-full block cursor-zoom-in outline-none" onClick={() => setViewer({ url: resolveMediaUrl(church.cover_url) })} aria-label={t("church.open_cover", "Voir la photo de couverture")} data-testid="church-cover-open">
                <img src={resolveMediaUrl(church.cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="church-cover" />
              </button>
            ) : (
              <ChurchCoverFallback church={church} className="absolute inset-0" testId="church-cover-default" />
            )}
            <button onClick={() => nav("/churches")} className="absolute top-3 left-3 h-9 w-9 rounded-full grid place-items-center z-20" style={{ background: "rgba(0,0,0,0.4)", color: "white" }} data-testid="church-back">
              <ArrowLeft size={17} />
            </button>
            {church.can_edit && (
              <div className="absolute bottom-3 right-3 z-20">
                <CoverEditMenu
                  currentUrl={church.cover_url ? resolveMediaUrl(church.cover_url) : null}
                  onUploaded={setCover}
                  onRemove={church.cover_url ? removeCover : undefined}
                  testId="church-cover-edit"
                  aspect={3}
                />
              </div>
            )}
          </div>
          <div className="px-4 md:px-6 pb-4 -mt-12">
            <div className="relative inline-block z-20">
              <div
                className={liveNow ? "rounded-2xl p-[3px]" : ""}
                style={liveNow ? { background: "conic-gradient(from 210deg, #e11d48, #f59e0b, #e11d48)" } : undefined}
                data-testid={liveNow ? "church-live-ring" : undefined}
              >
                {church.logo_url ? (
                  <button type="button" className="block rounded-2xl cursor-zoom-in outline-none" onClick={() => setViewer({ url: resolveMediaUrl(church.logo_url) })} aria-label={t("church.open_logo", "Voir le logo")} data-testid="church-logo-open">
                    <img src={resolveMediaUrl(church.logo_url)} alt={church.name} className="h-24 w-24 rounded-2xl object-cover shadow-lg border-4 border-white bg-white" data-testid="church-logo" />
                  </button>
                ) : (
                  <div className="h-24 w-24 rounded-2xl grid place-items-center shadow-lg border-4 border-white" style={{ background: "var(--yalola-secondary)" }} data-testid="church-logo-placeholder">
                    <Church size={38} className="text-white" />
                  </div>
                )}
              </div>
              {liveNow && (
                <button
                  onClick={() => nav(`/live?watch=${liveNow.id}`)}
                  className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide text-white px-2 py-0.5 rounded-full shadow transition-transform hover:scale-105"
                  style={{ background: "#e11d48" }}
                  data-testid="church-live-badge"
                  title={t("live.join", "Rejoindre le direct")}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> {t("embed.live_badge", "LIVE")}
                </button>
              )}
              {church.can_edit && (
                <div className="absolute -bottom-1 -right-1">
                  <ImageCropUpload aspect={1} cropShape="rect" onUploaded={setLogo} testId="church-logo-edit" title={t("church.adjust_logo", "Ajuster le logo de l'église")} />
                </div>
              )}
            </div>
            <div className="flex items-start justify-between gap-3 mt-3 flex-wrap">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 flex-wrap" style={{ fontFamily: "Outfit" }} data-testid="church-name">
                  {church.name}
                  {church.verified && <ShieldCheck size={20} style={{ color: "var(--yalola-accent)" }} />}
                  {church.featured && (
                    <span className="chip" style={{ background: "#E0A63C22", color: "var(--yalola-accent-text)", borderColor: "#E0A63C" }} data-testid="church-featured-badge">
                      <Sparkles size={13} /> {t("church.featured_badge", "À la une")}
                    </span>
                  )}
                  {church.managed_by_preacher && (
                    <span className="chip" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)", borderColor: "var(--yalola-primary)" }} data-testid="church-official-badge" title={church.manager_name ? t("church.managed_by_title", "Gérée par {name}").replace("{name}", church.manager_name) : t("church.official_page_title", "Page officielle")}>
                      <ShieldCheck size={13} /> {t("church.official_full", "Page officielle gérée par le pasteur")}
                    </span>
                  )}
                </h1>
                <div className="flex items-center gap-1.5 mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }}>
                  <MapPin size={14} /> {church.city}, {church.country}
                  {church.denomination && <span className="chip ml-1">{denominationLabel(t, church.denomination)}</span>}
                </div>
                {church.handle && (
                  <div className="mt-0.5 text-xs font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="church-handle">@{church.handle}</div>
                )}
                {church.parent_church && (
                  <button type="button" onClick={() => nav(`/churches/${church.parent_church.handle || church.parent_church.id}`)} className="chip mt-1.5 text-xs" style={{ background: "#EFF6FF", color: "#1E40AF", borderColor: "#93C5FD" }} data-testid="church-annex-of" data-parent-id={church.parent_church.id}>
                    <GitBranch size={12} /> {t("church.annex_of", "Annexe de {name}").replace("{name}", church.parent_church.name)}
                  </button>
                )}
                <div className="flex items-center gap-1.5 mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }}>
                  <Users size={14} /> <b style={{ color: "var(--yalola-text)" }}>{compactNumber(church.followers_count || 0)}</b> {t("church.followers_suffix", "abonné(s)")}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {canPublishPost && (
                  <PostComposer variant="button" churchId={church.id} churchName={church.name}
                    onPublished={(p) => setPosts((arr) => [p, ...arr])} />
                )}
                {church.can_favorite && (
                  <Button variant="outline" className="rounded-full" onClick={toggleFavorite} title="Favori personnel (admin) — visible par toi seulement" aria-pressed={!!church.is_favorite} data-testid="church-fav-btn">
                    <Star size={15} className="mr-1" fill={church.is_favorite ? "#E0A63C" : "none"} style={{ color: church.is_favorite ? "#E0A63C" : undefined }} /> {church.is_favorite ? "Dans mes favoris" : "Ajouter à mes favoris"}
                  </Button>
                )}
                {church.can_feature && (
                  <Button variant="outline" className="rounded-full" onClick={toggleFeature} data-testid="church-feature-btn">
                    <Sparkles size={15} className="mr-1" /> {church.featured ? t("church.unfeature", "Retirer de la une") : t("church.feature", "Mettre à la une")}
                  </Button>
                )}
                {church.can_edit && (
                  <>
                    <Button className="rounded-full pill-primary" onClick={() => nav(`/live?create=1&church=${id}`)} data-testid="church-go-live-btn">
                      <Radio size={15} className="mr-1" /> {t("church.go_live", "Passer en direct")}
                    </Button>
                    <Button variant="outline" className="rounded-full" onClick={() => setPromoteOpen(true)} data-testid="church-promote-btn">
                      <Megaphone size={15} className="mr-1" /> {t("church.promote", "Promouvoir")}
                    </Button>
                    <Button variant="outline" className="rounded-full" onClick={openEdit} data-testid="church-edit-btn">
                      <Pencil size={15} className="mr-1" /> {t("church.edit_page", "Éditer")}
                    </Button>
                  </>
                )}
                <Button
                  variant={church.is_following ? "outline" : "default"}
                  className={church.is_following ? "rounded-full" : "rounded-full pill-primary"}
                  onClick={toggleFollow}
                  data-testid="church-follow-btn"
                >
                  {church.is_following ? <><Check size={16} className="mr-1" /> {t("church.following_btn", "Suivie")}</> : <><Bell size={16} className="mr-1" /> {t("church.follow_btn", "Suivre")}</>}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setInviteOpen(true)}
                  data-testid="church-invite-btn"
                >
                  <UserPlus size={15} className="mr-1" /> {t("church.invite", "Inviter")}
                </Button>
              </div>
            </div>

            {church.can_favorite && church.is_favorite && (
              <div className="mt-2 flex items-center gap-2 text-xs rounded-lg px-2 py-1.5" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }} data-testid="church-fav-note-box">
                <span className="font-semibold shrink-0" style={{ color: "#92400E" }}>Ma note privée</span>
                <FavoriteNote churchId={church.id} note={church.favorite_note || ""} onSaved={(n) => setChurch((c) => ({ ...c, favorite_note: n }))} />
              </div>
            )}

            {(church.pastor_name || church.pastor_title) && (
              <div className="mt-3 text-sm flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}>
                <User size={14} /> {[church.pastor_title, church.pastor_name].filter(Boolean).join(" ")}
              </div>
            )}
            {church.description && <p className="mt-2 text-sm" style={{ color: "var(--yalola-text)" }}>{church.description}</p>}
            {church.address && <div className="mt-2 text-xs" style={{ color: "var(--yalola-text-muted)" }}>📍 {church.address}</div>}
            {church.website && (
              <a href={church.website} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs underline" style={{ color: "var(--yalola-primary)" }}>
                <Globe size={12} /> {church.website}
              </a>
            )}
          </div>
        </div>

        {/* Prédication vedette : la rediffusion la plus vue, en grande carte (masquée pendant un direct). */}
        {featured && !liveNow && <ChurchFeaturedReplay live={featured} church={church} total={livesTotal} />}

        {/* Lot 631 — église mère : ses annexes vérifiées. */}
        <ChurchAnnexes annexes={church.annexes} t={t} />

        {/* Lot 723 — cultes annulés (à venir) : visible par tous, « Rétablir » pour les responsables */}
        <ServiceCancellationsNotice churchId={id} cancellations={cancellations} canEdit={!!church.can_edit} onRestored={onRestored} />

        {/* Next recurring service — countdown banner (top of page) */}
        {nextService && (
          <div className="yalola-card p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="church-next-service"
               style={{ background: "var(--gradient-navy)", color: "white" }}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="shrink-0 h-11 w-11 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.15)" }}>
                <CalendarClock size={22} />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ opacity: 0.85 }}>{t("church.next_service", "Prochain culte")}</div>
                <div className="font-bold font-display truncate flex items-center gap-2" data-testid="church-next-service-title">
                  <span className="truncate">{nextService.item.title || t("church.service", "Culte")}</span>
                  {nextService.item.moved && <span className="chip text-[10px] shrink-0" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="church-next-service-moved">{t("church.moved_chip", "Déplacé")}</span>}
                  {nextService.item.special && <span className="chip text-[10px] shrink-0" style={{ background: "rgba(255,255,255,0.22)", color: "white" }} data-testid="church-next-service-special">{t("church.special_chip", "Exceptionnel")}</span>}
                </div>
                <div className="text-xs" style={{ opacity: 0.9 }}>{WEEKDAYS[nextService.item.day]}{(nextService.item.moved || nextService.item.special) ? ` ${nextService.date.getDate()}/${String(nextService.date.getMonth() + 1).padStart(2, "0")}` : ""} · {fmtTime(nextService.item.time, lang)}</div>
              </div>
            </div>
            <div className="shrink-0 text-right flex flex-col items-end gap-2">
              <div>
                <div className="text-[11px] uppercase tracking-wide" style={{ opacity: 0.8 }}>{t("church.starts_in", "Commence dans")}</div>
                <div className="text-lg font-bold font-display" data-testid="church-next-service-countdown">
                  {fmtCountdown(nextService.date.getTime() - nowTick, t) || t("church.now", "Maintenant")}
                </div>
              </div>
              {church.can_edit && (
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {!nextService.item.moved && !nextService.item.special && (
                    <Button size="sm" variant="outline" className="rounded-full" style={{ background: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.35)", color: "white" }} onClick={() => setCancelTarget(nextService)} data-testid="church-cancel-next-service-btn">
                      <CalendarX2 size={14} className="mr-1" /> {t("church.cancel_or_move", "Annuler / déplacer")}
                    </Button>
                  )}
                  <Button size="sm" className="rounded-full pill-primary" onClick={() => nav(`/live?create=1&church=${id}`)} data-testid="church-go-live-banner-btn">
                    <Radio size={14} className="mr-1" /> {t("church.go_live", "Passer en direct")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Agenda: recurring weekly services + upcoming dated events */}
        {(church.service_schedule?.length > 0 || events.length > 0 || church.can_edit) && (
          <div className="yalola-card p-5" data-testid="church-agenda-section">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <CalendarDays size={17} style={{ color: "var(--yalola-primary)" }} />
                <h2 className="font-bold font-display">{t("church.agenda", "Agenda")}</h2>
              </div>
              {church.can_edit && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={openSchedule} data-testid="church-edit-schedule-btn">
                    <Clock size={14} className="mr-1" /> {t("church.edit_services", "Cultes")}
                  </Button>
                  <Button size="sm" className="rounded-full pill-primary" onClick={() => setEventOpen(true)} data-testid="church-create-event-btn">
                    <CalendarPlus size={14} className="mr-1" /> {t("church.new_event", "Événement")}
                  </Button>
                </div>
              )}
            </div>

            {church.service_schedule?.length > 0 ? (
              <div className="mb-4" data-testid="church-services-list">
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--yalola-text-muted)" }}>{t("church.services_title", "Cultes réguliers")}</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {church.service_schedule.map((s, i) => {
                    const occ = nextOccurrenceOf(s);
                    const cancelled = occ ? cancellationFor(s, occ, cancellations) : null;
                    const shortDate = occ ? `${String(occ.getDate()).padStart(2, "0")}/${String(occ.getMonth() + 1).padStart(2, "0")}` : "";
                    return (
                      <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`church-service-${i}`}>
                        <div className="shrink-0 h-10 w-10 rounded-lg grid place-items-center font-bold text-xs" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>
                          {WEEKDAYS[s.day]?.slice(0, 3)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{s.title || t("church.service", "Culte")}</div>
                          <div className="text-xs flex items-center gap-2 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
                            <span>{WEEKDAYS[s.day]} · {fmtTime(s.time, lang)}</span>
                            {cancelled && cancelled.kind !== "moved" && <span className="chip text-[10px]" style={{ background: "#FEE2E2", color: "#B91C1C" }} data-testid={`church-service-cancelled-${i}`}>{t("church.cancelled_badge", "Annulé le {date}").replace("{date}", shortDate)}</span>}
                            {cancelled && cancelled.kind === "moved" && <span className="chip text-[10px]" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid={`church-service-moved-${i}`}>{t("church.moved_badge", "Déplacé au {date} {time}").replace("{date}", fmtDM(cancelled.new_date)).replace("{time}", cancelled.new_time || "")}</span>}
                          </div>
                        </div>
                        {church.can_edit && occ && !cancelled && (
                          <button type="button" className="shrink-0 text-[11px] font-semibold underline-offset-2 hover:underline" style={{ color: "#B91C1C" }} onClick={() => setCancelTarget({ item: s, date: occ })} data-testid={`church-service-cancel-${i}`}>
                            {t("church.cancel_next", "Annuler celui du {date}").replace("{date}", shortDate)}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : church.can_edit ? (
              <p className="text-xs mb-4" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-no-services">
                {t("church.no_services_owner", "Ajoutez vos horaires de culte pour que vos membres les retrouvent facilement.")}
              </p>
            ) : null}

            <h3 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--yalola-text-muted)" }}>{t("church.events_title", "Prochains événements")}</h3>
            {events.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-no-events">
                {t("church.no_events", "Aucun événement à venir pour le moment.")}
              </p>
            ) : (
              <div className="space-y-2" data-testid="church-events-list">
                {events.map((ev) => (
                  <div key={ev.id} className="flex items-center gap-3 rounded-xl border p-2.5" style={{ borderColor: "var(--yalola-border)" }} data-testid={`church-event-${ev.id}`}>
                    <div className="shrink-0 h-14 w-14 rounded-lg overflow-hidden border grid place-items-center" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}>
                      {ev.cover_url ? <img src={resolveMediaUrl(ev.cover_url)} alt="" className="h-full w-full object-cover" /> : <CalendarDays size={22} style={{ color: "var(--yalola-secondary)" }} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{ev.title}</div>
                      {ev.is_service && <span className="chip text-[10px] inline-flex items-center gap-1 mb-0.5" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`church-event-special-${ev.id}`}><Church size={10} /> {t("church.event_is_service", "Culte exceptionnel")}</span>}
                      <div className="text-xs flex items-center gap-1" style={{ color: "var(--yalola-primary)" }}><Clock size={12} /> {fmtEventDate(ev.starts_at)}</div>
                      {ev.location && <div className="text-xs truncate flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><MapPin size={11} /> {ev.location}</div>}
                      {(() => { const cd = countdownInfo(ev.starts_at, t); return cd ? (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold mt-1 ${cd.urgent ? "animate-pulse" : ""}`} style={{ background: "var(--yalola-primary)", color: "#fff" }} data-testid={`church-event-countdown-${ev.id}`}><Clock size={10} /> {cd.label}</span>
                      ) : null; })()}
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5">
                      <Button size="sm" variant={ev.is_attending ? "outline" : "default"} className={ev.is_attending ? "rounded-full" : "rounded-full pill-primary"} onClick={() => toggleAttend(ev)} data-testid={`church-event-attend-${ev.id}`}>
                        {ev.is_attending ? <><Check size={14} className="mr-1" /> {t("church.attending", "Je participe")}</> : t("church.attend", "Participer")}
                      </Button>
                      {ev.can_delete && (
                        <button onClick={() => deleteEvent(ev)} className="h-8 w-8 rounded-full grid place-items-center border" style={{ borderColor: "#DC2626", color: "#DC2626" }} data-testid={`church-event-delete-${ev.id}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Announcements (official church stories) */}
        <div className="yalola-card p-5">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Megaphone size={17} style={{ color: "var(--yalola-primary)" }} />
              <h2 className="font-bold font-display">{t("church.announcements_title", "Annonces & stories")}</h2>
            </div>
            {canPublishAnn && (
              <Button size="sm" className="rounded-full pill-primary" onClick={() => { setEditScheduled(null); setAnnOpen(true); }} data-testid="church-publish-announcement-btn">
                <Megaphone size={15} className="mr-1" /> {t("church.publish_announcement", "Publier une annonce")}
              </Button>
            )}
          </div>
          {canPublishAnn && scheduled.length > 0 && (
            <div className="mb-4 rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)", background: "rgba(0,0,0,0.02)" }} data-testid="church-scheduled-list">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={15} style={{ color: "var(--yalola-accent)" }} />
                <span className="text-sm font-semibold">{t("church.scheduled_title", "Annonces programmées")} ({scheduled.length})</span>
              </div>
              <div className="space-y-2">
                {scheduled.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 rounded-lg bg-white border px-3 py-2" style={{ borderColor: "var(--yalola-border)" }} data-testid={`church-scheduled-${s.id}`}>
                    <div className="shrink-0 h-11 w-11 rounded-lg overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }} data-testid={`church-scheduled-thumb-${s.id}`}>
                      {s.media_type === "image" && s.media_url ? (
                        <img src={resolveMediaUrl(s.media_url)} alt="" className="h-full w-full object-cover" />
                      ) : s.media_type === "video" && s.media_url ? (() => {
                        const u = resolveMediaUrl(s.media_url);
                        const isHls = u.includes(".m3u8") || u.includes("cloudflarestream.com");
                        return (
                          <div className="relative h-full w-full bg-black">
                            {isHls ? (
                              <img src={u.replace(/\/manifest\/video\.m3u8.*$/, "/thumbnails/thumbnail.jpg")} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <video
                                src={`${u}#t=0.1`}
                                poster={NEUTRAL_POSTER}
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                onMouseEnter={(e) => { e.currentTarget.play().catch(() => {}); }}
                                onMouseLeave={(e) => { e.currentTarget.pause(); try { e.currentTarget.currentTime = 0.1; } catch { /* noop */ } }}
                                className="h-full w-full object-cover"
                              />
                            )}
                            <div className="absolute inset-0 grid place-items-center pointer-events-none">
                              <PlayCircle size={16} className="text-white/90 drop-shadow" />
                            </div>
                          </div>
                        );
                      })() : (
                        <div className="h-full w-full grid place-items-center text-[7px] font-bold text-white leading-tight p-1 text-center overflow-hidden" style={{ ...(bgStyle(s.bg) || { background: "var(--yalola-primary)" }), ...storyFontStyle(s.font) }}>
                          {((s.text || "").trim() || "Aa").slice(0, 28)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{(s.text || "").trim() || (s.media_type === "video" ? t("church.video_item", "🎬 Vidéo") : t("church.visual_item", "🖼️ Visuel"))}</p>
                      <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                        📅 {s.scheduled_at ? new Date(s.scheduled_at).toLocaleString(lang || "fr", { dateStyle: "medium", timeStyle: "short" }) : ""}
                      </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5">
                      <button onClick={() => { setEditScheduled(s); setAnnOpen(true); }} className="text-xs rounded-full px-2.5 py-1 border" style={{ borderColor: "var(--yalola-primary)", color: "var(--yalola-primary)" }} data-testid={`church-scheduled-edit-${s.id}`}>
                        <Pencil size={12} className="inline mr-0.5" /> {t("church.edit", "Modifier")}
                      </button>
                      <button onClick={() => cancelScheduled(s.id)} className="text-xs rounded-full px-2.5 py-1 border" style={{ borderColor: "#DC2626", color: "#DC2626" }} data-testid={`church-scheduled-cancel-${s.id}`}>
                        <X size={13} className="inline mr-0.5" /> {t("church.cancel", "Annuler")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {canPublishAnn && <ChurchStoryTemplates churchId={church.id} t={t} />}
          {announcements.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-no-announcements">
              {t("church.no_announcements", "Aucune annonce en cours. Suis cette église pour être prévenu(e) des prochaines. 🙏")}
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-testid="church-announcements">
              {announcements.map((a, idx) => (
                <button
                  key={a.id}
                  onClick={() => setViewIdx(idx)}
                  className="relative rounded-xl overflow-hidden aspect-[9/16] border"
                  style={{ borderColor: "var(--yalola-border)" }}
                  data-testid={`church-ann-${a.id}`}
                >
                  {/* Même vignette que le Fil : fond/média + dessin + stickers */}
                  <StoryCover story={a} />
                  <span className="absolute bottom-1 right-1 text-[9px] text-white px-1.5 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.5)" }}>{timeAgo(a.created_at, t)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Lot 607 : gestionnaires délégués — responsable (ou admin) uniquement (module « church_managers ») */}
        {config?.settings?.features?.church_managers !== false && (church.can_manage_managers || church.is_owner || ["super_admin", "manager"].includes(user?.role)) && <ChurchManagersCard church={church} />}
        {/* Lot 608 : journal de la page — responsable (ou admin) uniquement (module « church_activity_log ») */}
        {config?.settings?.features?.church_activity_log !== false && (church.is_owner || ["super_admin", "manager", "moderator"].includes(user?.role)) && <ChurchActivityCard church={church} />}

        {/* Church stats dashboard (owner / admin only) */}
        {church.can_edit && church.stats_enabled && stats && (
          <div className="yalola-card p-5" data-testid="church-stats">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 size={17} style={{ color: "var(--yalola-primary)" }} />
              <h2 className="font-bold font-display">{t("church.stats_title", "Statistiques de l'église")}</h2>
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>{t("church.stats_subtitle", "Visible par vous et les administrateurs.")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { icon: Users, label: t("church.stat_followers", "Abonnés"), value: stats.followers, tid: "stat-followers" },
                { icon: Radio, label: t("church.stat_lives", "Directs"), value: stats.lives_count, tid: "stat-lives" },
                { icon: Eye, label: t("church.stat_views", "Vues (directs + replays)"), value: stats.total_views, tid: "stat-views" },
                { icon: TrendingUp, label: t("church.stat_peak", "Pic de spectateurs"), value: stats.peak_viewers, tid: "stat-peak" },
                { icon: Share2, label: t("church.stat_shares", "Partages"), value: stats.shares, tid: "stat-shares" },
              ].map((m) => (
                <div key={m.tid} className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`church-${m.tid}`}>
                  <m.icon size={18} className="mx-auto mb-1" style={{ color: "var(--yalola-secondary)" }} />
                  <div className="text-xl font-bold font-display" style={{ color: "var(--yalola-text)" }}>{compactNumber(m.value ?? 0)}</div>
                  <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lives & replays linked to this church (native videos) */}
        {(lives.length > 0 || church.can_edit) && (
          <div className="yalola-card p-5" data-testid="church-lives-section">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Radio size={17} style={{ color: "var(--yalola-primary)" }} />
                <h2 className="font-bold font-display">{t("church.lives_title", "Directs & rediffusions")}</h2>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {livesTotal > 1 && (
                  <div className="inline-flex rounded-full p-0.5 border" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} role="tablist" data-testid="church-lives-sort">
                    {[["recent", t("church.sort_recent", "Récents")], ["views", t("church.sort_most_viewed", "Les plus vues")]].map(([k, label]) => (
                      <button key={k} type="button" role="tab" aria-selected={livesSort === k} onClick={() => changeLivesSort(k)} className="h-8 px-3 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1" style={livesSort === k ? { background: "var(--yalola-primary)", color: "#fff" } : { color: "var(--yalola-text-muted)" }} data-testid={`church-lives-sort-${k}`}>
                        {k === "views" && <Flame size={12} />} {label}
                      </button>
                    ))}
                  </div>
                )}
                {church.can_edit && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={openAttach} data-testid="church-attach-my-lives-btn">
                    <Radio size={13} className="mr-1" /> {t("church.attach_my_lives", "Rattacher mes directs")}
                  </Button>
                )}
              </div>
            </div>
            {livesSort === "views" && lives.length > 0 && (
              <p className="text-xs -mt-1 mb-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-lives-top-hint">{t("church.top_hint", "Les prédications les plus regardées de cette église.")}</p>
            )}
            {lives.length === 0 && (
              <p className="text-sm mb-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-lives-empty">
                {t("church.no_lives_yet", "Aucun direct pour l'instant. Lance un direct « en tant que » cette église, ou rattache tes directs passés.")}
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-5" data-testid="church-lives-grid">
              {lives.slice(0, LIVES_PREVIEW).map((l, i) => (
                <ChurchReplayCard key={l.id} live={l} church={church} canEdit={!!church.can_edit} actions={liveActions} rank={livesSort === "views" && l.status !== "live" ? i + 1 : 0} />
              ))}
            </div>
            {livesTotal > LIVES_PREVIEW && (
              <div className="flex justify-center mt-4">
                <Button variant="outline" className="rounded-full" onClick={() => nav(`/churches/${id}/replays${livesSort === "views" ? "?sort=views" : ""}`)} data-testid="church-lives-view-all">
                  {t("church.view_all_replays", "Voir tout ({n})").replace("{n}", String(livesTotal))}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Owner: attach my own past lives/replays to this church page */}
        {cancelTarget && <CancelServiceDialog churchId={id} target={cancelTarget} onClose={() => setCancelTarget(null)} onDone={onCancelled} />}
        <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
          <DialogContent className="max-w-lg" data-testid="church-attach-dialog">
            <DialogHeader><DialogTitle>{t("church.attach_my_lives", "Rattacher mes directs")}</DialogTitle></DialogHeader>
            <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
              {t("church.attach_hint2", "Choisis un direct ou une rediffusion à faire apparaître dans « Directs & rediffusions ». Ceux déjà rattachés sont grisés.")}
            </p>
            <div className="space-y-2">
              {myUnattached.length === 0 && (
                <div className="py-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-attach-empty">
                  {t("church.attach_none", "Aucun direct à rattacher. Astuce : lance un direct « en tant que » cette église pour qu'il s'y ajoute automatiquement.")}
                </div>
              )}
              {(() => {
                const norm = (s) => (s || "").trim().toLowerCase();
                const onPage = new Set(lives.map((x) => norm(x.title)));
                return myUnattached.map((l) => {
                  const attachedHere = l.church_id === id;
                  const attachedElsewhere = !!l.church_id && l.church_id !== id;
                  const dupOnPage = !l.church_id && !!norm(l.title) && onPage.has(norm(l.title));
                  const disabled = attachedHere || attachedElsewhere || dupOnPage || !!attachBusy;
                  const label = attachedHere || dupOnPage
                    ? t("church.already_here", "Déjà sur cette page")
                    : attachedElsewhere
                      ? (l.church_name || t("church.already_attached", "Déjà rattaché"))
                      : (attachBusy === l.id ? "…" : t("church.attach_action", "Rattacher"));
                  return (
                    <div key={l.id} className="flex items-center gap-3 rounded-xl border p-2" style={{ borderColor: "var(--yalola-border)", opacity: (attachedHere || attachedElsewhere || dupOnPage) ? 0.6 : 1 }} data-testid={`church-attach-item-${l.id}`}>
                      <div className="h-12 w-20 shrink-0 rounded-lg overflow-hidden grid place-items-center" style={{ background: "#0A0D0C" }}>
                        {(l.poster_url || l.thumbnail_url)
                          ? <img src={resolveMediaUrl(l.poster_url || l.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                          : <Radio size={18} className="text-white/50" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{liveTitle(l, t, { name: l.church_name || user?.name })}</div>
                        <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
                          {l.status === "live" ? t("church.live_now", "● En direct") : t("church.replay_word", "Rediffusion")}{l.started_at ? ` · ${new Date(l.started_at).toLocaleDateString(lang || "fr")}` : ""}
                        </div>
                      </div>
                      <Button size="sm" className={`rounded-full shrink-0 ${disabled && !(attachBusy === l.id) ? "" : "pill-primary"}`} variant={(attachedHere || attachedElsewhere || dupOnPage) ? "outline" : "default"} onClick={() => attachMyLive(l.id)} disabled={disabled} data-testid={`church-attach-do-${l.id}`}>
                        {label}
                      </Button>
                    </div>
                  );
                });
              })()}
            </div>
          </DialogContent>
        </Dialog>

        {/* Posts published in the name of the church (its public wall) */}
        {posts.length > 0 && (
          <div data-testid="church-posts-section">
            <div className="flex items-center gap-2 mb-3 px-1">
              <Newspaper size={17} style={{ color: "var(--yalola-primary)" }} />
              <h2 className="font-bold font-display">{t("church.posts_title", "Publications")}</h2>
            </div>
            <div className="space-y-4">
              {posts.map((p, i) => (
                <FeedWindow key={p.id} index={i} testId={`feed-window-${p.id}`}>
                  <PostCard post={p}
                    onDeleted={(pid) => setPosts((arr) => arr.filter((x) => x.id !== pid))}
                    onHidden={(pid) => setPosts((arr) => arr.filter((x) => x.id !== pid))} />
                </FeedWindow>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Church edit dialog (owner / admin) */}
      <Dialog open={editing} onOpenChange={(v) => { if (!v) setEditing(false); }}>
        <DialogContent variant="sheet" data-testid="church-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t("church.edit_dialog_title", "Éditer la fiche — {name}").replace("{name}", church.name)}</DialogTitle>
          </DialogHeader>
          <DialogBody>
          {form && (
            <div className="space-y-4">
              <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                {t("church.media_hint", "La couverture et le logo se modifient directement sur la page (boutons appareil photo).")}
              </p>
              {/* Animated cover video — reserved for eligible ("large") churches */}
              {church.cover_video_eligible ? (
                <div data-testid="church-cover-video-section">
                  <Label>{t("church.cover_video", "Bannière vidéo")}</Label>
                  <div className="mt-1 rounded-xl overflow-hidden border relative" style={{ borderColor: "var(--yalola-border)" }}>
                    <div className="h-28 w-full grid place-items-center" style={{ background: "var(--gradient-navy)" }}>
                      {form.cover_video_url ? (
                        <video src={resolveMediaUrl(form.cover_video_url)} poster={videoPosterFor(resolveMediaUrl(form.cover_video_url), form.cover_url ? resolveMediaUrl(form.cover_url) : null)} className="h-full w-full object-cover" muted loop autoPlay playsInline />
                      ) : (
                        <Film size={26} className="text-white/70" />
                      )}
                    </div>
                    <div className="absolute bottom-2 right-2 flex gap-2">
                      {form.cover_video_url && <button onClick={() => setForm({ ...form, cover_video_url: "" })} className="text-xs bg-black/60 text-white rounded-full px-2 py-1" data-testid="church-cover-video-remove">{t("composer.remove", "Retirer")}</button>}
                      <FileUpload accept="video/*" onUploaded={(url) => setForm((f) => ({ ...f, cover_video_url: url }))} variant="compact" context="church_cover" buttonLabel={t("church.cover_video_add", "Ajouter une vidéo")} testId="church-cover-video-upload" />
                    </div>
                  </div>
                  <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("church.cover_video_hint", "Vidéo courte en boucle (≤ 30 s), muette. L'image de couverture sert de repli.")}</p>
                </div>
              ) : church.cover_video_enabled ? (
                <p className="text-[11px]" data-testid="church-cover-video-locked" style={{ color: "var(--yalola-text-muted)" }}>
                  <Film size={12} className="inline mr-1" />
                  {t("church.cover_video_locked", "Réservé aux églises d'au moins {n} abonnés.").replace("{n}", church.cover_video_min_followers)}
                </p>
              ) : null}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>{t("church.pastor_title_label", "Titre du responsable")}</Label><Input value={form.pastor_title} onChange={(e) => setForm({ ...form, pastor_title: e.target.value })} placeholder={t("church.pastor_title_ph", "Pasteur")} data-testid="church-edit-pastor-title" /></div>
                <div><Label>{t("church.pastor_name_label", "Nom du responsable")}</Label><Input value={form.pastor_name} onChange={(e) => setForm({ ...form, pastor_name: e.target.value })} placeholder="Patrick Kadiata" data-testid="church-edit-pastor-name" /></div>
              </div>
              <div><Label>{t("church.desc_label", "Description")}</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("church.desc_ph", "Présentez votre église...")} data-testid="church-edit-description" /></div>
              <div><Label>{t("church.address_label", "Adresse")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="12 rue Saint-Denis" data-testid="church-edit-address" /></div>
              <div><Label>{t("church.website_label", "Site web")}</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." data-testid="church-edit-website" /></div>
              <div>
                <Label>{t("church.handle_label", "Lien personnalisé (identifiant)")}</Label>
                <div className="flex items-center gap-1.5">
                  <span className="text-base font-bold" style={{ color: "var(--yalola-text-muted)" }}>@</span>
                  <Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value.replace(/^@+/, "").toLowerCase() })} placeholder={t("church.handle_ph", "mon-eglise")} data-testid="church-edit-handle" />
                </div>
                <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>
                  {t("church.handle_hint", "Ton lien de partage : {url}").replace("{url}", `${(typeof window !== "undefined" ? window.location.host : "yalola.app")}/churches/${form.handle || church.handle || id}`)}
                </p>
              </div>
              <div>
                <Label>{t("church.accent_label", "Couleur de la carte de partage")}</Label>
                <div className="flex items-center gap-2.5 mt-1.5">
                  {[
                    { key: "foret", name: t("poster.theme_foret", "Forêt"), color: "#1c3a2c" },
                    { key: "or", name: t("poster.theme_or", "Doré"), color: "#5a4413" },
                    { key: "nuit", name: t("poster.theme_nuit", "Nuit"), color: "#1e2a4a" },
                    { key: "royal", name: t("poster.theme_royal", "Royal"), color: "#3a1f52" },
                    { key: "aurore", name: t("poster.theme_aurore", "Aurore"), color: "#0f3f45" },
                  ].map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => setForm({ ...form, share_accent: a.key })}
                      title={a.name}
                      aria-label={a.name}
                      data-testid={`church-accent-${a.key}`}
                      className="h-9 w-9 rounded-full transition-transform hover:scale-110"
                      style={{ background: a.color, boxShadow: (form.share_accent || "foret") === a.key ? "0 0 0 2px var(--yalola-bg, #0E1A14), 0 0 0 4px var(--yalola-primary)" : "0 0 0 1px rgba(255,255,255,0.15)" }}
                    />
                  ))}
                </div>
                <p className="text-[11px] mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>
                  {t("church.accent_hint", "Sert de fond à la carte de partage quand aucune photo de couverture n'est définie.")}
                </p>
              </div>
            </div>
          )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(false)}>{t("common.cancel", "Annuler")}</Button>
            <Button className="rounded-full pill-primary" onClick={saveEdit} disabled={saving || !form} data-testid="church-edit-save">{saving ? t("church.saving", "Enregistrement…") : t("church.save", "Enregistrer")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote church page (owner / admin) — sponsored placement (demo, same flow as Ad Manager) */}
      <Dialog open={promoteOpen} onOpenChange={(v) => { if (!v) setPromoteOpen(false); }}>
        <DialogContent variant="sheet" className="sm:max-w-md" data-testid="church-promote-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("church.promote_dialog_title", "Promouvoir « {name} »").replace("{name}", church.name)}</DialogTitle></DialogHeader>
          <DialogBody className="space-y-3">
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("church.promote_desc", "Mets ta page en avant dans le fil comme une publication sponsorisée. Le budget est débité de tes crédits publicitaires.")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("church.budget_label", "Budget (€)")}</Label><Input type="number" min="1" value={promoteForm.budget} onChange={(e) => setPromoteForm({ ...promoteForm, budget: e.target.value })} data-testid="church-promote-budget" /></div>
            <div><Label>{t("church.duration_label", "Durée (jours)")}</Label><Input type="number" min="1" max="90" value={promoteForm.days} onChange={(e) => setPromoteForm({ ...promoteForm, days: e.target.value })} data-testid="church-promote-days" /></div>
          </div>
          {!promoteFree && <AdCreditBalance balance={creditBalance} currency={creditCur} need={Number(promoteForm.budget) || 0} loading={!credits} onBeforeNavigate={() => setPromoteOpen(false)} />}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPromoteOpen(false)}>{t("common.cancel", "Annuler")}</Button>
            <Button className="rounded-full pill-primary" onClick={doPromote} disabled={promoting || (!promoteFree && (Number(promoteForm.budget) || 0) > creditBalance)} data-testid="church-promote-confirm">{promoting ? "…" : t("church.pay_launch", "Lancer la campagne")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replay is now a dedicated full-page route (/replay/:id) */}

      {/* Visionneuse des annonces — même rendu que le Fil (StoryStage), ‹ › entre les annonces */}
      {viewIdx !== null && announcements[viewIdx] && (
        <ChurchStoryViewer
          stories={announcements}
          index={viewIdx}
          onIndexChange={setViewIdx}
          onClose={() => setViewIdx(null)}
          church={church}
          user={user}
          t={t}
          onDeleted={(sid) => { setAnnouncements((arr) => arr.filter((x) => x.id !== sid)); setViewIdx(null); }}
        />
      )}

      {/* Publish an official church announcement (owner preacher / admin) */}
      {canPublishAnn && (
        <StoryComposer
          open={annOpen}
          onOpenChange={(v) => { setAnnOpen(v); if (!v) { setEditScheduled(null); loadScheduled(); } }}
          onCreated={() => { setAnnOpen(false); load(); loadScheduled(); }}
          initialChurchId={church.id}
          initialAsChurch
          editStory={editScheduled}
        />
      )}

      {/* Church schedule (recurring services) edit dialog */}
      <Dialog open={scheduleOpen} onOpenChange={(v) => { if (!v) setScheduleOpen(false); }}>
        <DialogContent variant="sheet" data-testid="church-schedule-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("church.services_edit_title", "Cultes réguliers")}</DialogTitle></DialogHeader>
          <DialogBody className="space-y-3">
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("church.services_edit_hint", "Ajoutez vos créneaux récurrents (jour + heure + intitulé).")}</p>
          <div className="space-y-2">
            {scheduleRows.length === 0 && <p className="text-sm text-center py-3" style={{ color: "var(--yalola-text-muted)" }}>{t("church.services_empty", "Aucun créneau. Ajoutez-en un.")}</p>}
            {scheduleRows.map((row, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`church-schedule-row-${i}`}>
                <select value={row.day} onChange={(e) => updateRow(i, "day", Number(e.target.value))} className="rounded-lg border px-2 py-2 text-sm bg-white shrink-0" style={{ borderColor: "var(--yalola-border)" }} data-testid={`church-schedule-day-${i}`}>
                  {WEEKDAYS.map((d, di) => <option key={di} value={di}>{d}</option>)}
                </select>
                <Input type="time" value={row.time} onChange={(e) => updateRow(i, "time", e.target.value)} className="w-28 shrink-0" data-testid={`church-schedule-time-${i}`} />
                <Input value={row.title} onChange={(e) => updateRow(i, "title", e.target.value)} placeholder={t("church.service", "Culte")} data-testid={`church-schedule-title-${i}`} />
                <button onClick={() => removeRow(i)} className="shrink-0 h-9 w-9 rounded-full grid place-items-center border" style={{ borderColor: "#DC2626", color: "#DC2626" }} data-testid={`church-schedule-remove-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <div>
            <Button variant="outline" size="sm" className="rounded-full mt-1" onClick={addRow} data-testid="church-schedule-add"><Plus size={14} className="mr-1" /> {t("church.add_slot", "Ajouter un créneau")}</Button>
          </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setScheduleOpen(false)}>{t("common.cancel", "Annuler")}</Button>
            <Button className="rounded-full pill-primary" onClick={saveSchedule} disabled={savingSchedule} data-testid="church-schedule-save">{savingSchedule ? "…" : t("church.save", "Enregistrer")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create church event dialog */}
      <Dialog open={eventOpen} onOpenChange={(v) => { if (!v) setEventOpen(false); }}>
        <DialogContent variant="sheet" data-testid="church-event-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("church.new_event_title", "Nouvel événement")}</DialogTitle></DialogHeader>
          <DialogBody className="space-y-3">
            <div><Label>{t("church.event_title", "Titre")}</Label><Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder={t("church.event_title_ph", "Concert de louange")} data-testid="church-event-title" /></div>
            {/* Lot 725 — culte exceptionnel : bannière « Prochain culte » + rappel à tous les membres + notification dédiée */}
            <div className="flex items-start justify-between gap-3 rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }}>
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-1.5"><Church size={14} /> {t("church.event_is_service", "Culte exceptionnel")}</div>
                <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("church.event_is_service_hint", "Veillée, jeûne, fête… : apparaît comme « Prochain culte », rappel à tous les membres {h} h avant, notification immédiate + story automatique sur la page.").replace("{h}", String(config?.settings?.church?.service_reminder_hours || 2))}</div>
              </div>
              <Switch checked={!!eventForm.is_service} onCheckedChange={(v) => setEventForm({ ...eventForm, is_service: v })} data-testid="church-event-is-service" />
            </div>
            <div><Label>{t("church.event_datetime", "Date et heure")}</Label><Input type="datetime-local" value={eventForm.starts_at} onChange={(e) => setEventForm({ ...eventForm, starts_at: e.target.value })} data-testid="church-event-datetime" /></div>
            <div><Label>{t("church.event_location", "Lieu")}</Label><Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder={t("church.event_location_ph", "Adresse ou en ligne")} data-testid="church-event-location" /></div>
            <div><Label>{t("church.event_desc", "Description")}</Label><Textarea rows={3} value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} data-testid="church-event-desc" /></div>
            <div>
              <Label>{t("church.event_cover", "Visuel (facultatif)")}</Label>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                {eventForm.cover_url && <img src={resolveMediaUrl(eventForm.cover_url)} alt="" className="h-14 w-20 rounded-lg object-cover border" style={{ borderColor: "var(--yalola-border)" }} />}
                <FileUpload accept="image/*" onUploaded={(url) => setEventForm((f) => ({ ...f, cover_url: url }))} variant="compact" context="event_cover" buttonLabel={t("church.event_add_cover", "Ajouter un visuel")} testId="church-event-cover-upload" />
                {eventForm.cover_url && <button onClick={() => setEventForm((f) => ({ ...f, cover_url: "" }))} className="text-xs underline" style={{ color: "#DC2626" }}>{t("composer.remove", "Retirer")}</button>}
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEventOpen(false)}>{t("common.cancel", "Annuler")}</Button>
            <Button className="rounded-full pill-primary" onClick={createEvent} disabled={savingEvent} data-testid="church-event-save">{savingEvent ? "…" : t("church.event_publish", "Publier l'événement")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChurchInvitePoster open={inviteOpen} onOpenChange={setInviteOpen} church={church} />
      {viewer && <ImageViewer url={viewer.url} onClose={() => setViewer(null)} shareLink={`/churches/${church.id}`} />}
    </AppShell>
  );
}
